// Processing progress bar — pure helpers.
//
// The bar is a PURE function of (now − processing_started_at): no local startTime, so on a
// tab-change remount it reads the doc's anchor and paints the correct % immediately instead
// of restarting at 5%. Anchor fallback: processing_started_at (new docs) → created_at (old
// docs) → a captured local start (last resort, when both backend anchors are absent/invalid).
//
// TAU scales the ease-out curve with doc size, calibrated to real durations (≈3·TAU matches
// the p90 processing time per size bucket) so large docs don't pin at 95% too early.

export const PROCESSING_STALE_SECS = 300;

export function resolveAnchorMs(doc, localStartRef) {
  const parse = (v) => {
    const t = v ? Date.parse(v) : NaN;
    return Number.isNaN(t) ? null : t;
  };
  const anchor = parse(doc.processing_started_at) ?? parse(doc.created_at);
  if (anchor != null) return anchor;
  // Both backend anchors missing/invalid — capture a stable local start once (today's behavior).
  if (!localStartRef.current[doc.id]) localStartRef.current[doc.id] = Date.now();
  return localStartRef.current[doc.id];
}

export function computeProcessing(doc, now, localStartRef) {
  const elapsed = Math.max(0, (now - resolveAnchorMs(doc, localStartRef)) / 1000);
  const tl = doc.text_length;
  const TAU = (tl == null) ? 30 : Math.min(50, Math.max(20, 20 + tl / 1600));
  const pct = Math.min(95, Math.round(5 + 90 * (1 - Math.exp(-elapsed / TAU))));
  return { pct, stale: elapsed > PROCESSING_STALE_SECS };
}

// ── BARRA HONESTA (#370 a/b) ─────────────────────────────────────────────────────
// El backend escribe `progreso = {etapa, paginas_leidas, paginas_total, sello}` cuando cada
// etapa EMPIEZA (y una vez por página leída con los ojos). La barra se reescala por etapa:
// dentro de una etapa avanza suave por tiempo con la MISMA forma de la curva de arriba
// (TAU por tamaño), sin llegar al piso de la siguiente; nunca retrocede (máximo por
// documento en `honestoRef`); un `sello` viejo se ignora; 100 sólo lo estampa el poll al
// ver «Listo». Sin `progreso` (documentos en vuelo al desplegar, o sin marcas todavía) se
// cae a la fórmula de hoy, intacta.
export const ETAPAS_CON_PAGINAS = {
  leyendo: [2, 38], temas: [40, 52], preguntas: [52, 66], opciones: [66, 80], explicaciones: [80, 92], guardando: [92, 98],
};
export const ETAPAS_SIN_PAGINAS = {
  temas: [5, 25], preguntas: [25, 50], opciones: [50, 75], explicaciones: [75, 92], guardando: [92, 98],
};
const FIN_LEYENDO = 38;      // con todas las páginas leídas, suave 38 -> < 40 hasta `temas`
const PISO_TEMAS_CON_PAGINAS = 40;

export function tauDe(doc) {
  const tl = doc.text_length;
  return (tl == null) ? 30 : Math.min(50, Math.max(20, 20 + tl / 1600));
}

function suave(piso, techo, segundos, tau) {
  // La forma de `computeProcessing` reescalada al tramo [piso, techo), sin tocar `techo`.
  return piso + (techo - piso - 1) * (1 - Math.exp(-Math.max(0, segundos) / tau));
}

export function computeHonesto(doc, now, localStartRef, honestoRef) {
  const base = computeProcessing(doc, now, localStartRef);
  const registro = honestoRef.current;
  const reg = registro[doc.id] || (registro[doc.id] = {
    sello: -Infinity, etapa: null, inicio: now, leidas: 0, total: 0, fin: null, max: 0,
  });
  const p = doc.progreso;
  const etapa = (p && typeof p.etapa === 'string' && p.etapa) ? p.etapa : null;
  if (etapa) {
    const sello = Number(p.sello) || 0;
    if (sello >= reg.sello) {                       // igual o más nuevo: se acepta
      if (etapa !== reg.etapa) { reg.etapa = etapa; reg.inicio = now; reg.fin = null; }
      reg.sello = sello;
      reg.leidas = Math.max(reg.leidas, Number(p.paginas_leidas) || 0);
      reg.total = Math.max(reg.total, Number(p.paginas_total) || 0);
    }                                               // un sello viejo se ignora
  }
  if (!reg.etapa) {
    // Respaldo: la fórmula de hoy. Se recuerda lo pintado (con el tope de `leyendo` de la
    // tarjeta) para que la primera marca honesta nunca baje el número.
    const pintado = doc.lectura_estado === 'leyendo' ? Math.min(base.pct, 20) : base.pct;
    reg.max = Math.max(reg.max, pintado);
    return { pct: base.pct, stale: base.stale, etapa: null, n: 0, total: 0 };
  }
  const conPaginas = reg.total > 0 || Number(doc.paginas_ojos) > 0 || reg.etapa === 'leyendo';
  const tabla = conPaginas ? ETAPAS_CON_PAGINAS : ETAPAS_SIN_PAGINAS;
  const rango = tabla[reg.etapa];
  if (!rango) {
    reg.max = Math.max(reg.max, base.pct);
    return { pct: Math.max(base.pct, reg.max), stale: base.stale, etapa: null, n: 0, total: 0 };
  }
  const tau = tauDe(doc);
  let pct;
  if (reg.etapa === 'leyendo') {
    const total = reg.total;
    const leidas = Math.min(reg.leidas, total);
    pct = total > 0 ? 2 + 36 * (leidas / total) : 2;
    if (total > 0 && leidas >= total) {
      if (reg.fin == null) reg.fin = now;
      pct = suave(FIN_LEYENDO, PISO_TEMAS_CON_PAGINAS, (now - reg.fin) / 1000, tau);
    }
  } else {
    pct = suave(rango[0], rango[1], (now - reg.inicio) / 1000, tau);
  }
  pct = Math.max(Math.floor(pct), reg.max);
  reg.max = pct;
  // `n`/`total` solo tienen sentido en `leyendo` (el texto de las demas etapas no los usa).
  const enLectura = reg.etapa === 'leyendo';
  return { pct, stale: base.stale, etapa: reg.etapa, n: enLectura ? Math.min(reg.leidas, reg.total) : 0, total: enLectura ? reg.total : 0 };
}
